import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaSearch, FaStethoscope, FaPills, FaArrowRight, FaTimes } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import { BASE_URL } from "../backend/backend.js";

const images = [
  {
    src: "bg1.jpg", // Ensure this path is correct in your public folder
    title: "Your Health, Our Priority",
    description: "Get your medications delivered safely and quickly.",
  },
  {
    src: "bg2.jpg",
    title: "Consult a Doctor Anytime",
    description: "Talk to certified doctors from the comfort of your home.",
  },
  {
    src: "bg3.jpg",
    title: "High-Quality Medicines",
    description: "Your trusted source for top-rated medicines.",
  },
];

const MedicineCover = () => {
  const [currentImage, setCurrentImage] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [category, setCategory] = useState("Medicine");
  
  // Search API States
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  
  const searchContainerRef = useRef(null);
  const navigate = useNavigate();

  // Handle Carousel Rotation
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % images.length);
    }, 5000); // Auto change slide every 5 seconds
    return () => clearInterval(timer);
  }, []);

  // Debounced Search API Call
  useEffect(() => {
    if (!searchText.trim()) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    if (category !== "Medicine") return; // Only fetch if Medicine is selected

    setIsSearching(true);
    setShowDropdown(true);

    const delayDebounce = setTimeout(() => {
      fetch(`${BASE_URL}/api/filterMedicine?name=${encodeURIComponent(searchText)}&page=0&size=5`, {
        credentials: "include"
      })
        .then(async (res) => {
          if (!res.ok) throw new Error("No results");
          return res.json();
        })
        .then((data) => {
          const items = data.content ? data.content : (Array.isArray(data) ? data : []);
          setSearchResults(items);
          setIsSearching(false);
        })
        .catch(() => {
          setSearchResults([]);
          setIsSearching(false);
        });
    }, 400); // 400ms delay

    return () => clearTimeout(delayDebounce);
  }, [searchText, category]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div style={styles.container}>
      {/* Background Slideshow */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentImage}
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.2, ease: "easeInOut" }}
          style={{
            ...styles.bgImage,
            backgroundImage: `url(${images[currentImage].src})`,
          }}
        >
          {/* Dark Overlay for better text readability */}
          <div style={styles.overlay}></div>
        </motion.div>
      </AnimatePresence>

      {/* Main Content Box */}
      <div style={styles.contentWrapper}>
        <motion.div 
          initial={{ opacity: 0, y: 30 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.8 }}
          style={styles.glassCard}
        >
          <h1 style={styles.title}>{images[currentImage].title}</h1>
          <p style={styles.description}>{images[currentImage].description}</p>

          {/* Category Selector */}
          <div style={styles.categoryContainer}>
            {["Medicine", "Health Instrument", "Doctor"].map((cat) => (
              <button
                key={cat}
                onClick={() => { setCategory(cat); setSearchText(""); }}
                style={{
                  ...styles.categoryBtn,
                  ...(category === cat ? styles.categoryBtnActive : {}),
                }}
              >
                {cat === "Medicine" && <FaPills style={{ marginRight: "6px" }} />}
                {cat === "Doctor" && <FaStethoscope style={{ marginRight: "6px" }} />}
                {cat}
              </button>
            ))}
          </div>

          {/* Search Bar Container */}
          <div style={styles.searchWrapper} ref={searchContainerRef}>
            <div style={styles.inputContainer}>
              <FaSearch style={styles.searchIcon} />
              <input
                type="text"
                placeholder={`Search for ${category.toLowerCase()}...`}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onFocus={() => searchText.trim() && setShowDropdown(true)}
                style={styles.searchInput}
              />
              {searchText && (
                <FaTimes 
                  style={styles.clearIcon} 
                  onClick={() => setSearchText("")} 
                />
              )}
            </div>

            {/* Cute Dropdown for Results */}
            <AnimatePresence>
              {showDropdown && category === "Medicine" && (
                <motion.div
                  initial={{ opacity: 0, y: -10, scaleY: 0.9 }}
                  animate={{ opacity: 1, y: 0, scaleY: 1 }}
                  exit={{ opacity: 0, y: -10, scaleY: 0.9 }}
                  style={styles.dropdownMenu}
                >
                  {isSearching ? (
                    <div style={styles.dropdownState}>
                      <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1 }} style={{ display: 'inline-block' }}>
                        <FaSearch color="#0ea5e9" />
                      </motion.div>
                      <span style={{ marginLeft: "10px" }}>Searching...</span>
                    </div>
                  ) : searchResults.length > 0 ? (
                    searchResults.map((item) => (
                      <motion.div
                        key={item.id}
                        whileHover={{ backgroundColor: "#f0f9ff", x: 5 }}
                        style={styles.dropdownItem}
                       onClick={() => navigate("/mp", { state: { searchQuery: item.medicineName } })} // Navigate to medicine page
                      >
                        <div style={styles.itemInfo}>
                          <FaPills style={{ color: "#0ea5e9", fontSize: "1.2rem" }} />
                          <div>
                            <p style={styles.itemName}>{item.medicineName}</p>
                            <p style={styles.itemType}>{item.medineType || "Medicine"}</p>
                          </div>
                        </div>
                        <div style={styles.itemPrice}>
                          ₹{item.descountPrice || item.price}
                        </div>
                      </motion.div>
                    ))
                  ) : (
                    <div style={styles.dropdownState}>No medicines found for "{searchText}"</div>
                  )}
                  
                  {/* View All Footer */}
                  {searchResults.length > 0 && (
                    <div style={styles.dropdownFooter} onClick={() => navigate("/mp")}>
                      View all results <FaArrowRight style={{ marginLeft: '5px', fontSize: '0.8rem' }} />
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Action Buttons */}
          <div style={styles.actionButtons}>
            <motion.button
              whileHover={{ scale: 1.05, boxShadow: "0 10px 20px rgba(234, 88, 12, 0.4)" }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate("/mp")}
              style={{ ...styles.actionBtn, background: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)" }}
            >
              Order Medicine
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.05, boxShadow: "0 10px 20px rgba(16, 185, 129, 0.4)" }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate("/dc")}
              style={{ ...styles.actionBtn, background: "linear-gradient(135deg, #10b981 0%, #059669 100%)" }}
            >
              Consult Doctor
            </motion.button>
          </div>
        </motion.div>
      </div>

      {/* Slide Indicators */}
      <div style={styles.indicators}>
        {images.map((_, idx) => (
          <div
            key={idx}
            onClick={() => setCurrentImage(idx)}
            style={{
              ...styles.dot,
              backgroundColor: currentImage === idx ? "#f97316" : "rgba(255,255,255,0.4)",
              width: currentImage === idx ? "25px" : "10px",
            }}
          />
        ))}
      </div>
    </div>
  );
};

// Beautiful Modern Styling
const styles = {
  container: { position: "relative", height: "100vh", width: "100vw", overflow: "hidden", fontFamily: "'Inter', 'Poppins', sans-serif" },
  bgImage: { position: "absolute", top: 0, left: 0, width: "100%", height: "100%", backgroundSize: "cover", backgroundPosition: "center", zIndex: -1 },
  overlay: { position: "absolute", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(0, 0, 0, 0.4)" },
  contentWrapper: { position: "relative", zIndex: 10, height: "100%", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" },
  glassCard: { background: "rgba(255, 255, 255, 0.1)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", border: "1px solid rgba(255, 255, 255, 0.2)", borderRadius: "30px", padding: "50px 30px", maxWidth: "800px", width: "100%", textAlign: "center", boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)" },
  title: { fontSize: "clamp(2.5rem, 5vw, 4rem)", fontWeight: "800", color: "#ffffff", marginBottom: "15px", letterSpacing: "-1px", textShadow: "0 2px 10px rgba(0,0,0,0.3)" },
  description: { fontSize: "1.1rem", color: "#e2e8f0", marginBottom: "30px", maxWidth: "500px", margin: "0 auto 30px" },
  categoryContainer: { display: "flex", justifyContent: "center", gap: "10px", flexWrap: "wrap", marginBottom: "25px" },
  categoryBtn: { display: "flex", alignItems: "center", padding: "10px 20px", borderRadius: "20px", border: "1px solid rgba(255,255,255,0.3)", backgroundColor: "rgba(255,255,255,0.1)", color: "#fff", fontWeight: "600", cursor: "pointer", fontSize: "0.95rem", transition: "all 0.3s ease" },
  categoryBtnActive: { backgroundColor: "#fff", color: "#0f172a", borderColor: "#fff", boxShadow: "0 4px 15px rgba(255,255,255,0.2)" },
  searchWrapper: { position: "relative", maxWidth: "500px", margin: "0 auto", zIndex: 20 },
  inputContainer: { display: "flex", alignItems: "center", backgroundColor: "#fff", borderRadius: "50px", padding: "5px 20px", boxShadow: "0 10px 25px rgba(0,0,0,0.2)" },
  searchIcon: { color: "#64748b", fontSize: "1.2rem", marginRight: "10px" },
  clearIcon: { color: "#94a3b8", cursor: "pointer", transition: "color 0.2s", padding: "5px" },
  searchInput: { flex: 1, padding: "15px 0", border: "none", outline: "none", fontSize: "1.1rem", color: "#334155", background: "transparent" },
  dropdownMenu: { position: "absolute", top: "70px", left: "0", width: "100%", background: "#ffffff", borderRadius: "20px", boxShadow: "0 20px 40px rgba(0,0,0,0.3)", overflow: "hidden", transformOrigin: "top" },
  dropdownState: { padding: "20px", color: "#64748b", fontSize: "0.95rem", fontWeight: "500" },
  dropdownItem: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "15px 20px", borderBottom: "1px solid #f1f5f9", cursor: "pointer", transition: "background 0.2s" },
  itemInfo: { display: "flex", alignItems: "center", gap: "15px", textAlign: "left" },
  itemName: { margin: 0, fontWeight: "700", color: "#1e293b", fontSize: "1rem" },
  itemType: { margin: 0, fontSize: "0.75rem", color: "#94a3b8", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.5px", marginTop: "2px" },
  itemPrice: { fontWeight: "800", color: "#10b981", fontSize: "1.1rem" },
  dropdownFooter: { backgroundColor: "#f8fafc", padding: "12px", fontSize: "0.9rem", color: "#0ea5e9", fontWeight: "700", cursor: "pointer", display: "flex", justifyContent: "center", alignItems: "center" },
  actionButtons: { display: "flex", gap: "15px", justifyContent: "center", flexWrap: "wrap", marginTop: "35px" },
  actionBtn: { padding: "14px 32px", borderRadius: "30px", color: "white", border: "none", fontSize: "1.05rem", fontWeight: "700", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" },
  indicators: { position: "absolute", bottom: "30px", left: "50%", transform: "translateX(-50%)", display: "flex", gap: "10px", zIndex: 10 },
  dot: { height: "10px", borderRadius: "10px", cursor: "pointer", transition: "all 0.3s ease" }
};

export default MedicineCover;